import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  BookOpenText,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Copy,
  Gift,
  Heart,
  HelpCircle,
  LogIn,
  LogOut,
  MessageCircle,
  Package,
  PencilLine,
  Share2,
  ShieldCheck,
  Sparkles,
  Store,
  UserRound,
  type LucideIcon,
} from 'lucide-react'
import { api } from '@/lib/api'
import { isLodgingShop, localizedName } from '@/lib/format'
import { haptic } from '@/lib/telegram'
import { useAppStore } from '@/lib/store'
import { useT } from '@/lib/i18n'
import { useStoreTheme } from '@/lib/useStoreTheme'
import {
  StorefrontCard,
  StorefrontLoading,
  StorefrontPage,
} from '@/components/StorefrontPage'
import type { AdminSession, BookingOut, LodgingBooking, Order, StaticPage, WishlistItem } from '@/shared/types'

interface ReferralCode {
  code: string
  discount_percent: number
}

interface Profile {
  id: number
  name: string
  email: string
  phone: string
  language: string
  saved_delivery_type: string
  saved_city?: string
  saved_warehouse?: string
}

interface PublicSettings {
  services_enabled: boolean
  seller_registration_enabled: boolean
  referral_enabled?: boolean
  quiz_enabled?: boolean
  shop_name?: string
  bot_username?: string
}

type ProfileSession = AdminSession & {
  provider?: string
}

const LEGAL_SLUGS = new Set([
  'about',
  'delivery',
  'privacy-policy',
  'privacy',
  'terms',
  'terms-of-service',
  'rules',
  'dostavka-ta-oplata',
  'dostavka',
  'delivery-and-payment',
  'garantiya-ta-povernennya',
  'garantiya',
  'warranty',
  'warranty-and-returns',
  'oplata',
  'payment',
  'returns',
  'public-offer',
  'oferta',
])

function ActionCard({
  to,
  href,
  icon: Icon,
  title,
  hint,
  badge,
  onClick,
  disabled,
  highlight,
}: {
  to?: '/profile/edit' | '/orders' | '/bookings' | '/catalog' | '/login' | '/wishlist' | '/stories' | '/quiz'
  href?: string
  icon: LucideIcon
  title: string
  hint: string
  badge?: string | number
  onClick?: () => void
  disabled?: boolean
  highlight?: boolean
}) {
  const theme = useStoreTheme()

  let className = `flex items-center gap-3.5 rounded-2xl border px-4 py-3.5 text-left shadow-xs transition-all hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-60 `
  if (highlight) {
    className += 'border-[#bc9050]/40 bg-gradient-to-br from-[#2d333e] to-[#252b35] text-white hover:border-[#bc9050]'
  } else if (theme.isDarkTheme) {
    className += 'border-[#2d3748] bg-[#242c38] text-[#f8f3ea] hover:border-amber-500/40 hover:bg-[#293240]'
  } else {
    className += 'border-slate-200/80 bg-white hover:border-slate-300'
  }

  let iconStyle = 'bg-brand-50 text-brand-600'
  if (highlight) {
    iconStyle = 'bg-[#bc9050]/20 text-[#d7b171] ring-1 ring-[#bc9050]/30'
  } else if (theme.isDarkTheme) {
    iconStyle = 'bg-[#1a202c] text-amber-400 ring-1 ring-amber-500/20'
  }

  let titleStyle = 'text-gray-900'
  if (highlight || theme.isDarkTheme) {
    titleStyle = 'text-white'
  }

  let hintStyle = 'text-gray-500'
  if (highlight) {
    hintStyle = 'text-[#b9aa99]'
  } else if (theme.isDarkTheme) {
    hintStyle = 'text-slate-400'
  }

  let badgeStyle = 'bg-brand-100 text-brand-700'
  if (theme.isDarkTheme) {
    badgeStyle = 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
  }

  const content = (
    <>
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconStyle}`}>
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-sm font-semibold leading-tight ${titleStyle}`}>
          {title}
        </span>
        <span className={`block text-xs leading-normal mt-0.5 ${hintStyle}`}>
          {hint}
        </span>
      </span>
      {badge !== undefined && (
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${badgeStyle}`}>
          {badge}
        </span>
      )}
      <ChevronRight
        className={`h-4 w-4 shrink-0 ${highlight ? 'text-[#d7b171]' : theme.isDarkTheme ? 'text-slate-400' : 'text-gray-400'}`}
      />
    </>
  )

  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={className}>
        {content}
      </a>
    )
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} disabled={disabled} className={className}>
        {content}
      </button>
    )
  }
  return (
    <Link to={to!} className={className}>
      {content}
    </Link>
  )
}

export function ProfilePage() {
  const T = useT('profile')
  const common = useT('common')
  const lang = useAppStore((s) => s.lang)
  const setLang = useAppStore((s) => s.setLang)
  const queryClient = useQueryClient()
  const [isRulesExpanded, setIsRulesExpanded] = useState(false)
  const theme = useStoreTheme()

  const { data: pages } = useQuery<StaticPage[]>({
    queryKey: ['static-pages'],
    queryFn: () => api.get<StaticPage[]>('/pages'),
    staleTime: 300_000,
  })

  const { data: settings } = useQuery<PublicSettings>({
    queryKey: ['public-settings'],
    queryFn: () => api.get<PublicSettings>('/settings'),
    staleTime: 300_000,
  })

  const {
    data: session,
    isLoading: isSessionLoading,
    isError: isSessionError,
  } = useQuery<ProfileSession>({
    queryKey: ['admin-session-preview'],
    queryFn: () => api.get<ProfileSession>('/auth/me'),
    retry: false,
  })

  const authResolved = isSessionError || Boolean(session)
  const isGuestSession =
    isSessionError || session?.provider === 'guest' || session?.role === 'guest'
  const canLoadUserData = authResolved && !isGuestSession
  const referralEnabled = settings?.referral_enabled ?? true

  const { data: referral } = useQuery<ReferralCode>({
    queryKey: ['referral-code'],
    queryFn: () => api.get<ReferralCode>('/me/referral-code'),
    staleTime: Infinity,
    enabled: referralEnabled && canLoadUserData,
  })

  const { data: profile, isLoading, isError } = useQuery<Profile>({
    queryKey: ['profile'],
    queryFn: () => api.get<Profile>('/me'),
    enabled: canLoadUserData,
  })

  const { data: orders } = useQuery<Order[]>({
    queryKey: ['orders'],
    queryFn: () => api.get<Order[]>('/orders'),
    enabled: canLoadUserData,
  })

  const { data: wishlist } = useQuery<WishlistItem[]>({
    queryKey: ['wishlist'],
    queryFn: () => api.get<WishlistItem[]>('/wishlist'),
  })

  const isLodging = isLodgingShop(settings?.shop_name)
  const { data: lodgingBookings } = useQuery<LodgingBooking[]>({
    queryKey: ['lodging-my-bookings'],
    queryFn: () => api.get<LodgingBooking[]>('/lodging/bookings'),
    enabled: isLodging && canLoadUserData,
  })

  const servicesEnabled = Boolean(settings?.services_enabled)
  const { data: bookings } = useQuery<BookingOut[]>({
    queryKey: ['my-bookings'],
    queryFn: () => api.get<BookingOut[]>('/bookings/my'),
    enabled: servicesEnabled && canLoadUserData,
  })

  const sellerRegistrationEnabled = Boolean(settings?.seller_registration_enabled)
  const becomeSellerMutation = useMutation({
    mutationFn: () => api.post('/seller/register', {}),
    onSuccess: () => {
      haptic('success')
      queryClient.invalidateQueries({ queryKey: ['admin-session-preview'] })
      window.location.href = '/seller'
    },
    onError: () => haptic('error'),
  })

  const isMayParfum = theme.isMayParfum

  const educationalPages = useMemo(() => {
    return (pages ?? []).filter((page) => {
      const slug = page.slug?.toLowerCase() ?? ''
      return !LEGAL_SLUGS.has(slug)
    })
  }, [pages])

  const legalPages = useMemo(() => {
    return (pages ?? []).filter((page) => {
      const slug = page.slug?.toLowerCase() ?? ''
      return LEGAL_SLUGS.has(slug)
    })
  }, [pages])

  function handleCopyReferral() {
    if (!referral?.code) return
    haptic('light')
    void navigator.clipboard.writeText(referral.code)
  }

  function handleShareReferral() {
    if (!referral?.code) return
    haptic('light')
    const shareText = `Приєднуйся до покупок за моїм промокодом ${referral.code} та отримай знижку ${referral.discount_percent}%!`
    if (navigator.share) {
      void navigator.share({
        title: 'Реферальна програма',
        text: shareText,
      })
    } else {
      void navigator.clipboard.writeText(shareText)
    }
  }

  const logoutMutation = useMutation({
    mutationFn: () => api.post('/auth/logout', {}),
    onSuccess: () => {
      window.dispatchEvent(new CustomEvent('auth:logout'))
      window.location.href = '/app/login'
    },
  })

  if (isSessionLoading || !authResolved || (!isGuestSession && isLoading)) {
    return (
      <StorefrontPage>
        <StorefrontLoading message={common.loading} />
      </StorefrontPage>
    )
  }
  if (!isGuestSession && isError) {
    return (
      <StorefrontPage>
        <p className="p-4 text-center text-xs font-semibold text-rose-500">{T.error}</p>
      </StorefrontPage>
    )
  }

  const contact = isGuestSession
    ? T.guestContact
    : profile?.phone || profile?.email || T.noContact
  const delivery = profile?.saved_delivery_type
    ? T.deliveryLabels[profile.saved_delivery_type] ?? profile.saved_delivery_type
    : T.noDelivery
  const displayName = isGuestSession ? T.guestName : profile?.name || T.nameFallback
  const showOrders = !isGuestSession && (Boolean(orders?.length) || Boolean(lodgingBookings?.length))
  const showBookings = servicesEnabled && !isGuestSession && Boolean(bookings?.length)
  const showAdmin = Boolean(session?.can_admin)
  const showBecomeSeller = sellerRegistrationEnabled && !isGuestSession && !showAdmin
  const wishlistCount = wishlist?.length ?? 0
  const ordersCount = orders?.length ?? 0
  const supportUrl = settings?.bot_username
    ? `https://t.me/${settings.bot_username}`
    : 'https://t.me/mayparfum_bot'

  return (
    <StorefrontPage className="mx-auto max-w-2xl">
      {/* User Header Profile Card */}
      <StorefrontCard className={`mb-6 p-4 ${
        theme.isDarkTheme
          ? 'bg-[#242c38] border-[#2d3748]'
          : 'bg-gradient-to-br from-white via-slate-50 to-brand-50/50'
      }`}>
        <div className="flex items-start gap-3.5">
          <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl shadow-2xs ${
            theme.isDarkTheme
              ? 'bg-[#1a202c] text-amber-400 ring-1 ring-amber-500/20'
              : 'bg-white text-brand-600 ring-1 ring-slate-100'
          }`}>
            <UserRound className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className={`text-base font-bold ${theme.isDarkTheme ? 'text-[#f8f3ea]' : 'text-slate-900'}`}>{displayName}</p>
              {referral && (
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                  theme.isDarkTheme ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-emerald-100 text-emerald-800'
                }`}>
                  -{referral.discount_percent}%
                </span>
              )}
            </div>
            <p className={`truncate text-xs mt-0.5 ${theme.isDarkTheme ? 'text-slate-400' : 'text-slate-500'}`}>{contact}</p>
            {!isGuestSession && (
              <div className={`mt-2 space-y-0.5 border-t pt-2 text-xs ${
                theme.isDarkTheme ? 'border-[#3a4352]/70 text-slate-300' : 'border-slate-100 text-slate-600'
              }`}>
                <p className={`font-semibold ${theme.isDarkTheme ? 'text-slate-200' : 'text-slate-800'}`}>
                  🚚 {delivery}
                </p>
                {Boolean(profile?.saved_city) && (
                  <p className={theme.isDarkTheme ? 'text-slate-400' : 'text-slate-600'}>
                    📍 {profile?.saved_city}{profile?.saved_warehouse ? `, ${profile.saved_warehouse}` : ''}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </StorefrontCard>

      {/* Fragrance Quiz / Olfactory Profile Highlight (MayParfum) */}
      {isMayParfum && (
        <section className="mb-6">
          <ActionCard
            to="/quiz"
            icon={Sparkles}
            title={T.perfumeQuizCta}
            hint={T.perfumeQuizCtaHint}
            highlight={true}
          />
        </section>
      )}

      {/* SECTION: My Activity */}
      <section className="mb-6 space-y-2.5">
        <h2 className={`px-1 text-xs font-bold uppercase tracking-wider ${theme.isDarkTheme ? 'text-slate-400' : 'text-gray-400'}`}>
          {T.myActivity}
        </h2>
        {isGuestSession ? (
          <ActionCard to="/login" icon={LogIn} title={T.login} hint={T.loginHint} />
        ) : (
          <ActionCard
            to="/profile/edit"
            icon={PencilLine}
            title={T.editProfile}
            hint={T.editProfileHint}
          />
        )}
        {showOrders && (
          <ActionCard
            to="/orders"
            icon={Package}
            title={T.myOrders}
            hint={T.myOrdersHint}
            badge={ordersCount > 0 ? ordersCount : undefined}
          />
        )}
        {showBookings && (
          <ActionCard
            to="/bookings"
            icon={CalendarDays}
            title={T.myBookings}
            hint={T.myBookingsHint}
          />
        )}
        {wishlistCount > 0 && (
          <ActionCard
            to="/wishlist"
            icon={Heart}
            title={T.myWishlist}
            hint={T.myWishlistHint}
            badge={wishlistCount}
          />
        )}
      </section>

      {/* SECTION: Referral Program */}
      {referralEnabled && referral && (
        <section className={`mb-6 rounded-3xl border p-4 shadow-sm ${
          theme.isDarkTheme ? 'border-[#2d3748] bg-[#242c38] text-[#f8f3ea]' : 'border-gray-200 bg-white'
        }`}>
          <div className="flex items-start gap-3">
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
              theme.isDarkTheme ? 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/20' : 'bg-brand-50 text-brand-600'
            }`}>
              <Gift className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-semibold ${theme.isDarkTheme ? 'text-white' : 'text-gray-900'}`}>{T.referralTitle}</p>
              <p className={`mt-0.5 text-xs ${theme.isDarkTheme ? 'text-slate-400' : 'text-gray-500'}`}>
                {T.referralHint(referral.discount_percent)}
              </p>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <code className={`min-w-0 flex-1 truncate rounded-xl px-3 py-2 text-sm font-semibold ${
              theme.isDarkTheme ? 'bg-[#1a202c] text-amber-300 border border-[#3a4352]' : 'bg-gray-50 text-gray-900'
            }`}>
              {referral.code}
            </code>
            <button
              type="button"
              onClick={handleCopyReferral}
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition ${
                theme.isDarkTheme ? 'border-[#3a4352] text-slate-300 hover:bg-[#1a202c]' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
              aria-label={T.referralCopy}
            >
              <Copy className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleShareReferral}
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition ${
                theme.isDarkTheme ? 'border-[#3a4352] text-slate-300 hover:bg-[#1a202c]' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
              aria-label={T.referralShare}
            >
              <Share2 className="h-4 w-4" />
            </button>
          </div>
        </section>
      )}

      {/* SECTION: Knowledge Base & Guides (Clean Single Entry) */}
      {(isMayParfum || educationalPages.length > 0) && (
        <section className="mb-6 space-y-2.5">
          <h2 className={`px-1 text-xs font-bold uppercase tracking-wider ${theme.isDarkTheme ? 'text-slate-400' : 'text-gray-400'}`}>
            {T.guidesTitle}
          </h2>
          <ActionCard
            to="/stories"
            icon={BookOpenText}
            title={isMayParfum ? T.perfumeGuides : T.articlesAndGuides}
            hint={
              isMayParfum
                ? T.perfumeGuidesHint
                : T.articlesAndGuidesHint(educationalPages.length)
            }
            badge={educationalPages.length > 0 ? educationalPages.length : (isMayParfum ? 7 : undefined)}
          />
        </section>
      )}

      {/* SECTION: Service & Store Information */}
      <section className="mb-6 space-y-2.5">
        <h2 className={`px-1 text-xs font-bold uppercase tracking-wider ${theme.isDarkTheme ? 'text-slate-400' : 'text-gray-400'}`}>
          {T.helpAndRules}
        </h2>
        <ActionCard
          href={supportUrl}
          icon={MessageCircle}
          title={T.supportTelegram}
          hint={T.supportTelegramHint}
        />

        {/* Compact Legal/Store Info Collapsible Card */}
        {legalPages.length > 0 && (
          <div className={`overflow-hidden rounded-2xl border shadow-sm ${
            theme.isDarkTheme ? 'border-[#2d3748] bg-[#242c38] text-[#f8f3ea]' : 'border-gray-200 bg-white'
          }`}>
            <button
              type="button"
              onClick={() => {
                haptic('light')
                setIsRulesExpanded((v) => !v)
              }}
              className={`flex w-full items-center justify-between p-4 text-left transition ${
                theme.isDarkTheme ? 'hover:bg-[#2a3442]' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3.5">
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                  theme.isDarkTheme ? 'bg-[#1a202c] text-amber-400 ring-1 ring-amber-500/20' : 'bg-gray-100 text-gray-600'
                }`}>
                  <HelpCircle className="h-5 w-5" />
                </span>
                <div>
                  <p className={`text-sm font-semibold ${theme.isDarkTheme ? 'text-white' : 'text-gray-900'}`}>{T.storeInfoAndRules}</p>
                  <p className={`text-xs ${theme.isDarkTheme ? 'text-slate-400' : 'text-gray-500'}`}>{T.storeInfoAndRulesHint}</p>
                </div>
              </div>
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${
                  isRulesExpanded ? 'rotate-180' : ''
                } ${theme.isDarkTheme ? 'text-slate-400' : 'text-gray-400'}`}
              />
            </button>

            {isRulesExpanded && (
              <div className={`divide-y px-4 py-2 border-t ${
                theme.isDarkTheme
                  ? 'divide-[#2d3748] border-[#2d3748] bg-[#1a202c]/70'
                  : 'divide-gray-100 border-gray-100 bg-gray-50/50'
              }`}>
                {legalPages.map((page) => (
                  <Link
                    key={page.slug}
                    to="/pages/$slug"
                    params={{ slug: page.slug }}
                    className={`flex items-center justify-between py-2.5 text-xs font-semibold transition ${
                      theme.isDarkTheme
                        ? 'text-slate-300 hover:text-amber-300'
                        : 'text-gray-700 hover:text-brand-600'
                    }`}
                  >
                    <span>{localizedName(lang, page.title_uk, page.title_en)}</span>
                    <ChevronRight className={`h-3.5 w-3.5 ${theme.isDarkTheme ? 'text-slate-400' : 'text-gray-400'}`} />
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* SECTION: Account Settings & Preferences */}
      <section className="mb-6 space-y-2.5">
        <h2 className={`px-1 text-xs font-bold uppercase tracking-wider ${theme.isDarkTheme ? 'text-slate-400' : 'text-gray-400'}`}>
          {T.accountSettings}
        </h2>

        {/* In-Profile Fast Language Switcher */}
        <div className={`flex items-center justify-between rounded-2xl border p-3.5 shadow-sm ${
          theme.isDarkTheme ? 'border-[#2d3748] bg-[#242c38] text-[#f8f3ea]' : 'border-gray-200 bg-white'
        }`}>
          <span className={`text-sm font-semibold ${theme.isDarkTheme ? 'text-white' : 'text-gray-900'}`}>{T.interfaceLanguage}</span>
          <div className={`flex items-center gap-1 rounded-xl p-1 ${theme.isDarkTheme ? 'bg-[#1a202c]' : 'bg-gray-100'}`}>
            {(['uk', 'en', 'ru'] as const).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => {
                  haptic('light')
                  setLang(l)
                }}
                className={`rounded-lg px-2.5 py-1 text-xs font-bold uppercase transition ${
                  lang === l
                    ? theme.isDarkTheme
                      ? 'bg-amber-500 text-[#17120f] font-bold shadow-sm'
                      : 'bg-white text-gray-900 shadow-sm'
                    : theme.isDarkTheme
                      ? 'text-slate-400 hover:text-white'
                      : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {showAdmin && (
          <ActionCard
            href="/admin"
            icon={ShieldCheck}
            title={T.openAdmin}
            hint={T.openAdminHint}
          />
        )}

        {showBecomeSeller && (
          <ActionCard
            onClick={() => becomeSellerMutation.mutate()}
            disabled={becomeSellerMutation.isPending}
            icon={Store}
            title={T.createStore}
            hint={becomeSellerMutation.isPending ? T.becomingSeller : T.createStoreHint}
          />
        )}

        {!isGuestSession && (
          <ActionCard
            onClick={() => logoutMutation.mutate()}
            disabled={logoutMutation.isPending}
            icon={LogOut}
            title={T.logout}
            hint={logoutMutation.isPending ? T.loggingOut : T.logoutHint}
          />
        )}
      </section>
    </StorefrontPage>
  )
}
